import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { createPrepaid } from '../../api/operatingCosts'
import AccountSelect, { costAccount, useAccounts } from '../../components/AccountSelect'
import ErrorBanner from '../../components/ErrorBanner'
import { HelpField, HelpTip, LabelWithHelp } from '../../components/HelpTip'
import { BackLink, DocHeader } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { useControlAccounts, useStandardDefaults } from './accountingShared'

// Add a prepaid: a cost paid in advance, moved into expenses monthly.
export default function PrepaidForm() {
  const nav = useNavigate()
  const { notify } = useToast()
  const accounts = useAccounts()
  const controls = useControlAccounts()
  const std = useStandardDefaults()
  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState(localToday())
  const [amount, setAmount] = useState('')
  const [months, setMonths] = useState('12')
  const [prepaidCode, setPrepaidCode] = useState('')
  const [expenseCode, setExpenseCode] = useState('')
  const [recordNow, setRecordNow] = useState(true)
  const [creditCode, setCreditCode] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!controls || !accounts.length) return
    const pick = (...codes) => codes.find((c) => c && accounts.some((a) => a.account_code === c)) || ''
    setPrepaidCode((c) => c || pick(controls.PrepaidAsset, std.PrepaidAsset))
    setExpenseCode((c) => c || pick(controls.PrepaidExpense, std.PrepaidExpense))
    setCreditCode((c) => c || pick(controls.Cash, std.Cash))
  }, [controls, accounts, std])

  const total = num(amount)
  const n = parseInt(months, 10)
  const perMonth = total > 0 && n > 0 ? total / n : 0

  const save = async () => {
    setError('')
    if (!name.trim()) { setError('Give the prepaid a name.'); return }
    if (!(total > 0)) { setError('Enter the amount paid.'); return }
    if (!(n > 0)) { setError('Enter how many months it covers.'); return }
    if (!prepaidCode || !expenseCode) { setError('Choose the prepaid and expense accounts.'); return }
    if (recordNow && !creditCode) { setError('Choose the account it was paid from.'); return }
    setSaving(true)
    try {
      const res = await createPrepaid({
        name: name.trim(), start_date: startDate, amount_total: total, months: n, frequency: 'Monthly',
        prepaid_account_code: prepaidCode, expense_account_code: expenseCode,
        record_prepaid_now: recordNow, ...(recordNow ? { credit_account_code: creditCode } : {}),
      })
      notify(res?.je_id ? `${name.trim()} added, payment posted as ${docNo('je', res.je_no)}` : `${name.trim()} added`)
      nav('/accounting/prepaids', { replace: true })
    } catch (e) {
      setError(e.message || 'Could not save the prepaid.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/accounting/prepaids" label="Prepaids" />} title={<>Add a prepaid <HelpTip k="prepaid" /></>} />

      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card acc-stack">
        <div className="ci-form-meta-top two">
          <label className="ci-form-field">
            <span>What was paid for</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Shop insurance 2026" />
          </label>
          <label className="ci-form-field">
            <span>First month</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
        </div>
        <div className="ci-form-meta-top two">
          <label className="ci-form-field">
            <span>Amount paid</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" />
          </label>
          <label className="ci-form-field">
            <span>Months it covers</span>
            <input value={months} onChange={(e) => setMonths(e.target.value)} inputMode="numeric" />
          </label>
        </div>
        {perMonth > 0 && <p className="doc-hint acc-monthly">{money(perMonth)} a month moves into expenses <HelpTip k="amortization" /></p>}
      </div>

      <div className="ci-form-meta-card">
        <div className="acc-card-title">Accounts</div>
        <div className="ci-form-meta-top two">
          <HelpField label="Prepaid account" k="prepaidAccount">
            <AccountSelect value={prepaidCode} onChange={setPrepaidCode} types={['Asset']} />
          </HelpField>
          <HelpField label="Expense account" k="prepaidExpenseAccount">
            <AccountSelect value={expenseCode} onChange={setExpenseCode} types={['Expense', 'Asset']} filter={costAccount} />
          </HelpField>
        </div>
      </div>

      <div className="ci-form-meta-card">
        <label className="ci-form-toggle acc-toggle">
          <input type="checkbox" checked={recordNow} onChange={(e) => setRecordNow(e.target.checked)} />
          <LabelWithHelp label="Post the payment entry now" k="recordPrepaid" />
        </label>
        {recordNow && (
          <div className="ci-form-meta-top two acc-tight">
            <label className="ci-form-field">
              <span>Paid from (credit)</span>
              <AccountSelect value={creditCode} onChange={setCreditCode} types={['Asset', 'Liability']} />
            </label>
          </div>
        )}
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button data-write type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}><Check size={14} />{saving ? 'Saving…' : 'Add prepaid'}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(-1)}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
